/* Variant-aware filter counts for collection/search facets.
 * Shopify's availability filter is product-level: a product can count for a
 * filter value if it owns that value and has any in-stock variant. This
 * rewrites rendered facet counts from available variants so option values
 * have to be in stock on the same variant, while vendor/type/tag counts still
 * respect the active available-variant option filters. It also hides grid
 * cards whose only matching variant isn't a strict, in-stock match for an
 * active Size/Length-style filter (see applyGridVisibility) — Shopify's
 * grouped filter values (e.g. "US 9" also covering "US 9 Wide") are correct
 * for the filter UI, but shouldn't widen what actually shows as a result.
 */
(function () {
  'use strict';

  if (window.__sonderVariantCountsBooted) return;
  window.__sonderVariantCountsBooted = true;

  var CONFIG_SELECTOR = '[data-variant-count-config]';
  var ROW_SELECTOR = '[data-variant-count-row]';
  var MAX_PAGES = 8;
  var cache = {};
  var running = false;
  var queued = false;

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function compact(value) {
    return norm(value)
      .replace(/&nbsp;/g, ' ')
      /* Gendered region prefixes ("USW 6.5", "US W 8") must be stripped the
       * same as their bare form ("US 6.5") — Shopify's own FilterSettingGroup
       * grouping already treats them as the same filter value (shared GID),
       * so the client-side recount has to agree or it undercounts any row
       * whose in-stock variants only exist in the gendered form. */
      .replace(/\b(usw|us\s*w|ukw|uk\s*w|euw|eu\s*w|us|uk|eu)\s*(?=\d)/g, '')
      .replace(/^(usw|us\s*w|ukw|uk\s*w|euw|eu\s*w|us|uk|eu)(?=\d)/, '')
      .replace(/\b(usw|us\s*w|ukw|uk\s*w|euw|eu\s*w|us|uk|eu|cm|mm|mens|men's|womens|women's)\b/g, '')
      .replace(/[^a-z0-9.]+/g, '');
  }

  function optionKey(value) {
    return norm(value).replace(/colour/g, 'color');
  }

  function optionName(param) {
    return optionKey(decodeURIComponent(String(param || '').replace(/^filter\.v\.option\./, '').replace(/\+/g, ' ')));
  }

  function dimInfo(param) {
    if (param.indexOf('filter.v.option.') === 0) return { type: 'option', name: optionName(param) };
    if (param === 'filter.p.vendor' || param === 'filter.p.product_vendor') return { type: 'vendor' };
    if (param === 'filter.p.product_type') return { type: 'type' };
    if (param === 'filter.p.tag') return { type: 'tag' };
    if (param.indexOf('filter.p.m.') === 0) return { type: 'product_metafield' };
    if (param.indexOf('filter.v.m.') === 0) return { type: 'variant_metafield' };
    return { type: 'other' };
  }

  function readConfig() {
    var el = document.querySelector(CONFIG_SELECTOR);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (err) { return null; }
  }

  function rowMatcher(param, value) {
    var selector = ROW_SELECTOR + '[data-filter-param="' + cssEscape(param) + '"][data-filter-value="' + cssEscape(value) + '"]';
    var row = document.querySelector(selector);
    var label = row && row.getAttribute('data-filter-label');
    return makeMatcher(value, label || value, dimInfo(param));
  }

  function rowPriority(row) {
    var param = row.getAttribute('data-filter-param') || '';
    var groupLabel = row.getAttribute('data-filter-group-label') || '';
    var info = dimInfo(param);
    var haystack = optionKey((info.name || '') + ' ' + groupLabel);
    return info.type === 'option' && /size|length/.test(haystack) ? 0 : 1;
  }

  function activeFilters() {
    var filters = {};
    var search = new URLSearchParams(window.location.search);
    search.forEach(function (value, param) {
      if (param === 'filter.v.availability') return;
      if (param.indexOf('filter.v.price.') === 0) return;
      if (param.indexOf('filter.') !== 0) return;
      if (!filters[param]) filters[param] = { info: dimInfo(param), matchers: [] };
      filters[param].matchers.push(rowMatcher(param, value));
    });
    return filters;
  }

  function parseRange(text) {
    var raw = norm(text);
    var numbers = raw.match(/\d+(?:\.\d+)?/g);
    if (!numbers || !numbers.length) return null;
    if (numbers.length >= 2 && /-|–|—|\bto\b/.test(raw)) {
      return { min: parseFloat(numbers[0]), max: parseFloat(numbers[1]) };
    }
    if (/\+|and up|over|above|greater/.test(raw)) {
      return { min: parseFloat(numbers[0]), max: Infinity };
    }
    if (/under|below|less/.test(raw)) {
      return { min: -Infinity, max: parseFloat(numbers[0]) };
    }
    return null;
  }

  function numericValue(text) {
    var match = norm(text).match(/\d+(?:\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }

  function makeMatcher(value, label, info) {
    var values = [value, label].filter(Boolean);
    var range = parseRange(label) || parseRange(value);
    var isSized = info.type === 'option' && /size|length/.test(info.name);
    return function (raw) {
      var rawNorm = norm(raw);
      for (var i = 0; i < values.length; i++) {
        if (rawNorm === norm(values[i])) return true;
        if (isSized && compact(raw) === compact(values[i])) return true;
      }
      if (range) {
        var num = numericValue(raw);
        if (num != null && num >= range.min && num <= range.max) return true;
      }
      return false;
    };
  }

  function attrValues(product, info) {
    if (info.type === 'vendor') return product.vendor ? [product.vendor] : [];
    if (info.type === 'type') return product.type ? [product.type] : [];
    if (info.type === 'tag') return product.tags || [];
    return [];
  }

  function canCountClientSide(info) {
    return info.type === 'option' || info.type === 'vendor' || info.type === 'type' || info.type === 'tag';
  }

  function anyMatch(values, matchers) {
    for (var i = 0; i < values.length; i++) {
      for (var j = 0; j < matchers.length; j++) {
        if (matchers[j](values[i])) return true;
      }
    }
    return false;
  }

  function productPasses(product, active, skipParam) {
    for (var param in active) {
      if (!Object.prototype.hasOwnProperty.call(active, param) || param === skipParam) continue;
      var filter = active[param];
      if (filter.info.type === 'vendor' || filter.info.type === 'type' || filter.info.type === 'tag') {
        if (!anyMatch(attrValues(product, filter.info), filter.matchers)) return false;
      }
    }
    return true;
  }

  function optionValues(variant, option) {
    return variant.options[option] || [];
  }

  function variantPasses(variant, active, skipParam, candidate) {
    if (!variant.available) return false;
    for (var param in active) {
      if (!Object.prototype.hasOwnProperty.call(active, param) || param === skipParam) continue;
      var filter = active[param];
      if (filter.info.type !== 'option') continue;
      if (!anyMatch(optionValues(variant, filter.info.name), filter.matchers)) return false;
    }
    if (candidate && candidate.info.type === 'option') {
      return candidate.matcher && anyMatch(optionValues(variant, candidate.info.name), [candidate.matcher]);
    }
    return true;
  }

  function countProducts(products, param, matcher, active) {
    var info = dimInfo(param);
    if (!canCountClientSide(info)) return null;
    var count = 0;
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (!productPasses(product, active, param)) continue;
      if (info.type === 'vendor' || info.type === 'type' || info.type === 'tag') {
        if (!anyMatch(attrValues(product, info), [matcher])) continue;
      }
      var candidate = info.type === 'option' ? { info: info, matcher: matcher } : null;
      for (var v = 0; v < product.variants.length; v++) {
        if (variantPasses(product.variants[v], active, param, candidate)) {
          count++;
          break;
        }
      }
    }
    return count;
  }

  function setRowCount(row, count) {
    var countEl = row.querySelector('.brutal-filter-count');
    if (countEl) countEl.textContent = '(' + count + ')';
    var active = row.classList.contains('is-active') || row.classList.contains('link_active') || row.querySelector('.is-selected, .brutal-filter-check--active');
    if (count === 0 && !active) {
      row.hidden = true;
      row.setAttribute('data-variant-count-hidden', '');
    } else if (row.hasAttribute('data-variant-count-hidden')) {
      row.hidden = false;
      row.removeAttribute('data-variant-count-hidden');
    }
  }

  function productQualifies(product, active) {
    if (!productPasses(product, active, null)) return false;
    for (var v = 0; v < product.variants.length; v++) {
      if (variantPasses(product.variants[v], active, null, null)) return true;
    }
    return false;
  }

  function currentResultCount(products, active) {
    var count = 0;
    for (var i = 0; i < products.length; i++) {
      if (productQualifies(products[i], active)) count++;
    }
    return count;
  }

  /* Shopify's own Size/Length filter values are grouped (e.g. "US 9" also
   * covers "US 9 Wide", "US 9 Warehouse Direct", "US 9 (Faded Display)"),
   * which is correct for the filter UI but means the server-rendered grid
   * can include products whose only matching variant isn't a strict,
   * in-stock match for what the shopper actually selected. This hides
   * grid cards that don't pass the same strict variant-level check already
   * used for the sidebar counts, without touching the grouping itself. */
  var GRID_CARD_SELECTOR = '.brutal-product-grid [product-id]';

  function numericId(gid) {
    var match = String(gid || '').match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  function restoreCard(card) {
    if (card.hasAttribute('data-variant-count-hidden')) {
      card.hidden = false;
      /* The theme's own grid/flex CSS sets `display` directly on the card
       * (e.g. .brutal-card { display: flex }), which has equal-or-higher
       * specificity than the browser's built-in `[hidden] { display:none }`
       * rule and wins — so the `hidden` property alone doesn't reliably
       * hide anything here. Clearing the inline override we set below. */
      card.style.display = '';
      card.removeAttribute('data-variant-count-hidden');
    }
  }

  function toggleEmptyState(isEmpty) {
    var grid = document.querySelector('.brutal-product-grid');
    if (!grid) return;
    var msg = document.querySelector('[data-variant-count-empty]');
    if (isEmpty) {
      if (!msg) {
        msg = document.createElement('div');
        msg.setAttribute('data-variant-count-empty', '');
        msg.className = 'empty-state empty-state--tight';
        msg.innerHTML = '<p class="empty-state__description">No products match this size in stock.</p>';
        grid.parentNode.insertBefore(msg, grid.nextSibling);
      }
      msg.hidden = false;
    } else if (msg) {
      msg.hidden = true;
    }
  }

  function applyGridVisibility(products, active) {
    var cards = document.querySelectorAll(GRID_CARD_SELECTOR);
    if (!cards.length) return;

    var hasOptionFilter = false;
    for (var param in active) {
      if (Object.prototype.hasOwnProperty.call(active, param) && active[param].info.type === 'option') {
        hasOptionFilter = true;
        break;
      }
    }
    if (!hasOptionFilter) {
      Array.prototype.forEach.call(cards, restoreCard);
      toggleEmptyState(false);
      return;
    }

    var qualifying = {};
    products.forEach(function (product) {
      if (product.numericId && productQualifies(product, active)) qualifying[product.numericId] = true;
    });

    var visible = 0;
    Array.prototype.forEach.call(cards, function (card) {
      var id = card.getAttribute('product-id');
      if (!id || qualifying[id]) {
        restoreCard(card);
        visible++;
      } else {
        card.hidden = true;
        /* Inline style wins over any theme CSS that sets `display`
         * directly on the card (see restoreCard). */
        card.style.display = 'none';
        card.setAttribute('data-variant-count-hidden', '');
      }
    });

    toggleEmptyState(visible === 0);
  }

  function updateResultLabels(count) {
    document.querySelectorAll('.brutal-active-bar__count, .collection-drawer__footer-count').forEach(function (el) {
      var formatted = count.toLocaleString();
      var text = el.textContent || '';
      if (/[\d,]+/.test(text)) {
        el.textContent = text.replace(/[\d,]+/, formatted);
      } else {
        el.textContent = formatted + (count === 1 ? ' result' : ' results');
      }
    });
  }

  function applyRows(rows, products, active) {
    rows.forEach(function (row) {
      var param = row.getAttribute('data-filter-param');
      var value = row.getAttribute('data-filter-value');
      var label = row.getAttribute('data-filter-label') || value;
      var info = dimInfo(param);
      if (!canCountClientSide(info)) return;
      var count = countProducts(products, param, makeMatcher(value, label, info), active);
      if (count != null) setRowCount(row, count);
    });
  }

  function applyCounts(products) {
    if (!products || !products.length) return;
    var active = activeFilters();
    var priorityRows = [];
    var remainingRows = [];
    Array.prototype.slice.call(document.querySelectorAll(ROW_SELECTOR)).forEach(function (row) {
      (rowPriority(row) === 0 ? priorityRows : remainingRows).push(row);
    });
    /* Previously staggered remainingRows/result-label/grid-visibility behind
     * requestAnimationFrame to let priority (Size/Length) rows paint first.
     * rAF is unreliable when the tab loses focus or is throttled (a shopper
     * glancing away right after clicking a filter is enough), which left
     * brand/type counts and the product grid stuck on stale values while
     * only the priority rows updated. Run everything synchronously instead —
     * all rows are computed from the already-fetched in-memory product list,
     * so there's no real performance reason to defer any of it. */
    applyRows(priorityRows, products, active);
    applyRows(remainingRows, products, active);
    updateResultLabels(currentResultCount(products, active));
    applyGridVisibility(products, active);
  }

  function storefrontFilters() {
    var filters = [{ available: true }];
    var price = {};
    var hasPrice = false;
    var search = new URLSearchParams(window.location.search);
    search.forEach(function (value, param) {
      if (param === 'filter.v.price.gte') { price.min = parseFloat(value); hasPrice = true; }
      if (param === 'filter.v.price.lte') { price.max = parseFloat(value); hasPrice = true; }
      var serverFilter = storefrontFilter(param, value);
      if (serverFilter) filters.push(serverFilter);
    });
    if (hasPrice) filters.push({ price: price });
    return filters;
  }

  function metafieldFilter(prefix, param, value) {
    if (String(param || '').indexOf(prefix) !== 0) return null;
    var keyPath = String(param || '').slice(prefix.length);
    var parts = keyPath.split('.');
    if (parts.length < 2) return null;
    var filter = {
      namespace: parts.shift(),
      key: parts.join('.'),
      value: value
    };
    if (!filter.namespace || !filter.key || filter.value == null || filter.value === '') return null;
    return filter;
  }

  function storefrontFilter(param, value) {
    var productMetafield = metafieldFilter('filter.p.m.', param, value);
    if (productMetafield) {
      if (productMetafield.namespace === 'shopify') return { taxonomyMetafield: productMetafield };
      return { productMetafield: productMetafield };
    }
    var variantMetafield = metafieldFilter('filter.v.m.', param, value);
    if (variantMetafield) return { variantMetafield: variantMetafield };
    return null;
  }

  function productFields() {
    return 'id vendor productType tags variants(first:100){nodes{availableForSale selectedOptions{name value}}}';
  }

  function buildQuery(type) {
    if (type === 'search') {
      return 'query VariantCounts($q:String!,$filters:[ProductFilter!],$after:String){search(query:$q,first:250,types:[PRODUCT],productFilters:$filters,after:$after){nodes{... on Product{' + productFields() + '}}pageInfo{hasNextPage endCursor}}}';
    }
    return 'query VariantCounts($handle:String!,$filters:[ProductFilter!],$after:String){collection(handle:$handle){products(first:250,filters:$filters,after:$after){nodes{' + productFields() + '}pageInfo{hasNextPage endCursor}}}}';
  }

  function gql(query, variables) {
    var token = window.theme && window.theme.storefrontToken;
    var version = (window.theme && window.theme.storefrontApiVersion) || '2025-01';
    if (!token) return Promise.reject(new Error('missing Storefront token'));
    return fetch('/api/' + version + '/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (response) { return response.json(); });
  }

  function mapProduct(node) {
    if (!node) return null;
    return {
      id: node.id,
      numericId: numericId(node.id),
      vendor: node.vendor,
      type: node.productType,
      tags: node.tags || [],
      variants: ((node.variants && node.variants.nodes) || []).map(function (variant) {
        var options = {};
        (variant.selectedOptions || []).forEach(function (option) {
          var key = optionKey(option.name);
          if (!options[key]) options[key] = [];
          options[key].push(option.value);
        });
        return { available: !!variant.availableForSale, options: options };
      })
    };
  }

  function fetchProducts(config) {
    var filters = storefrontFilters();
    var key = JSON.stringify({
      type: config.type,
      handle: config.handle || '',
      terms: config.terms || '',
      filters: filters
    });
    if (cache[key]) return Promise.resolve(cache[key]);

    var query = buildQuery(config.type);
    var products = [];

    function step(after, page) {
      var vars = config.type === 'search'
        ? { q: config.terms || '', filters: filters, after: after }
        : { handle: config.handle, filters: filters, after: after };
      return gql(query, vars).then(function (json) {
        if (!json || json.errors || !json.data) throw new Error('Storefront query failed');
        var connection = config.type === 'search'
          ? json.data.search
          : (json.data.collection && json.data.collection.products);
        if (!connection || !connection.nodes) throw new Error('Missing product connection');
        connection.nodes.forEach(function (node) {
          var product = mapProduct(node);
          if (product) products.push(product);
        });
        if (connection.pageInfo && connection.pageInfo.hasNextPage && page < MAX_PAGES) {
          return step(connection.pageInfo.endCursor, page + 1);
        }
        cache[key] = products;
        return products;
      });
    }

    return step(null, 1);
  }

  function run() {
    var config = readConfig();
    if (!config || !document.querySelector(ROW_SELECTOR)) return;
    if (running) { queued = true; return; }
    running = true;
    fetchProducts(config)
      .then(applyCounts)
      .catch(function () {})
      .then(function () {
        running = false;
        if (queued) { queued = false; run(); }
      });
  }

  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
